<!--
  Property panel for the selected builder node.

  Controls arrive grouped by the catalog. Groups are collapsible, and the ones open on arrival are
  the ones a merchant came for — placement, content, behaviour — so a seventeen-control element
  reads as a short list rather than a wall. A control declaring `target: 'parent'` edits the
  containing group instead: where an element sits inside its row is stored on the row, but a
  merchant looks for it on the element they clicked.
-->
<template>
  <div class="flex flex-col gap-4">
    <div v-if="!node" class="text-sm text-typography-disabled py-6 text-center">
      Select an element to edit its properties.
    </div>

    <template v-else>
      <div class="flex items-center gap-2">
        <component :is="icon" class="w-4 h-4 fill-path-current text-primary-700" />
        <span class="text-sm font-semibold text-typography-primary">{{ displayName }}</span>
      </div>

      <!--
        `emits` is what carries a click to the bus, so a button missing it does nothing at all —
        whatever Action says. Picking an Action writes both.
      -->
      <p v-if="inertMessage" class="text-2xs text-warning-700 bg-warning-100 rounded px-2 py-1.5">
        {{ inertMessage }}
      </p>

      <p v-if="schema?.notice" class="text-2xs text-typography-secondary bg-neutral-100 rounded px-2 py-1.5">
        {{ schema.notice }}
      </p>

      <!-- Turn this element into a compatible one, keeping its wording, styling and behaviour. -->
      <div v-if="convertOptions.length" class="flex flex-col gap-1">
        <Dropdown
          :model-value="node.type"
          :items="convertOptions"
          label="Element type"
          @update:model-value="v => convertTo(String(v))"
        />
        <p class="text-2xs text-typography-disabled">
          {{ convertHint }}
        </p>
      </div>

      <!-- Unknown element type: no schema, surface the raw attributes read-only -->
      <div v-if="!schema" class="flex flex-col gap-2">
        <p class="text-xsm text-warning-700">Unknown element type "{{ node.type }}". Editing is unavailable.</p>
        <pre class="text-2xs bg-neutral-100 rounded p-2 overflow-auto max-h-40">{{ rawAttributes }}</pre>
      </div>

      <template v-else>
        <div v-for="group in groupedControls" :key="group.name" class="flex flex-col gap-3">
          <button
            v-if="group.name"
            type="button"
            class="flex items-center justify-between text-2xs font-semibold uppercase tracking-wide text-typography-disabled hover:text-typography-secondary"
            @click="toggleGroup(group.name)"
          >
            <span>{{ group.name }}</span>
            <component :is="isOpen(group.name) ? ICON_EXPANDED : ICON_COLLAPSED" class="w-3 h-3 fill-path-current" />
          </button>

          <template v-if="isOpen(group.name)">
            <BuilderControl
              v-for="control in group.controls"
              :key="`${control.target ?? 'self'}:${control.attr}`"
              :control="control"
              :model-value="valueFor(control)"
              :error="copyErrorFor(control)"
              @update:model-value="v => onChange(control, v)"
            />
          </template>
        </div>

        <div v-if="visibleControls.length === 0" class="text-xsm text-typography-disabled">
          This element has no editable properties.
        </div>

        <!-- Read-only event hints -->
        <div v-if="schema.emitsHints?.length || schema.listensHints?.length" class="flex flex-col gap-2 pt-2">
          <div v-if="schema.emitsHints?.length" class="flex flex-wrap items-center gap-1">
            <span class="text-2xs text-typography-disabled">Emits:</span>
            <span
              v-for="e in schema.emitsHints"
              :key="e"
              class="text-2xs bg-primary-100 text-primary-700 rounded px-1.5 py-0.5"
              >{{ e }}</span
            >
          </div>
          <div v-if="schema.listensHints?.length" class="flex flex-wrap items-center gap-1">
            <span class="text-2xs text-typography-disabled">Listens:</span>
            <span
              v-for="l in schema.listensHints"
              :key="l"
              class="text-2xs bg-neutral-200 text-neutral-700 rounded px-1.5 py-0.5"
              >{{ l }}</span
            >
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Dropdown } from '@pulse/components';
import BuilderControl from './BuilderControl.vue';
import { effectiveDirection, resolveAxisControl, resolveElementIcon } from '~/builder/catalog';
import type { CatalogMap, ControlSchema } from '~/builder/catalog';
import { ICON_EXPANDED, ICON_COLLAPSED } from '~/builder/catalog/icons';
import { clickIsInert, type BuilderNode } from '~/builder/useBuilderTree';
import { conversionLosses } from '~/builder/useBuilderConvert';
import { INCLUDED_COPY_ERROR, isForbiddenIncludedCopy } from '~/utils/included-copy-guard';

/**
 * Groups that answer the questions a merchant actually arrives with — where does this sit, what
 * does it say, what does it do. They open regardless of position; styling detail stays collapsed.
 */
const PRIMARY_GROUPS = ['Placement', 'Content', 'Behavior'];

/** Fallback for a schema that uses none of the primary group names. */
const OPEN_GROUP_COUNT = 2;

const props = defineProps<{
  node: BuilderNode | null;
  catalog: CatalogMap;
  /** The containing element, when the node has one. Enables `target: 'parent'` controls. */
  parent?: BuilderNode | null;
  /** An Included widget, whose copy must not read as a discount. */
  guardIncludedCopy?: boolean;
}>();

const emit = defineEmits<{
  change: [attr: string, value: string | number | boolean];
  'change-parent': [attr: string, value: string | number | boolean];
  convert: [targetType: string];
}>();

const schema = computed(() => (props.node ? props.catalog[props.node.type] : undefined));

const displayName = computed(() => schema.value?.displayName || props.node?.type || 'Element');

const icon = computed(() => resolveElementIcon(schema.value?.icon));

// Every string attribute is guarded, so the error shows on whichever control holds the offending copy.
function copyErrorFor(control: ControlSchema): string | undefined {
  if (!props.guardIncludedCopy || control.target === 'parent') return undefined;

  return isForbiddenIncludedCopy(props.node?.attributes?.[control.attr]) ? INCLUDED_COPY_ERROR : undefined;
}

const rawAttributes = computed(() => JSON.stringify(props.node?.attributes ?? {}, null, 2));

/** A parent-targeted control only makes sense when that parent exists and is the right kind. */
function appliesToParent(control: ControlSchema): boolean {
  if (control.target !== 'parent') return true;
  if (!props.parent) return false;

  if (control.parentTypes && !control.parentTypes.includes(props.parent.type)) return false;

  return !control.parentDirection || control.parentDirection === effectiveDirection(props.parent);
}

function isTruthy(value: unknown): boolean {
  if (value === true) return true;

  return typeof value === 'string' && value.trim() !== '' && value !== 'false';
}

/** A control gated on another attribute stays hidden until that gate is satisfied. */
function isUnlocked(control: ControlSchema): boolean {
  // Never hide a control that already holds a value, or the merchant is stranded with a setting
  // they can see the effect of but cannot reach to change or clear.
  if (isTruthy(props.node?.attributes?.[control.attr])) return true;

  if (control.showWhen && !isTruthy(props.node?.attributes?.[control.showWhen])) return false;

  const match = control.showWhenMatches;
  if (match) {
    const against = props.node?.attributes?.[match.attr];
    if (typeof against !== 'string' || !new RegExp(match.pattern, 'i').test(against)) return false;
  }

  return true;
}

/**
 * An axis control's wording comes from whichever container it writes to — the parent for a
 * parent-targeted control, the node itself for its own layout — so "Horizontal align" never
 * appears on an axis that is actually vertical.
 */
function withResolvedAxis(control: ControlSchema): ControlSchema {
  return resolveAxisControl(control, control.target === 'parent' ? props.parent : props.node);
}

const visibleControls = computed(() =>
  (schema.value?.controls ?? []).filter(c => appliesToParent(c) && isUnlocked(c)).map(withResolvedAxis),
);

// Group controls by their `group`, preserving first-seen order.
const groupedControls = computed(() => {
  const groups: { name: string; controls: ControlSchema[] }[] = [];
  const byName = new Map<string, ControlSchema[]>();

  for (const control of visibleControls.value) {
    const name = control.group ?? '';
    if (!byName.has(name)) {
      const controls: ControlSchema[] = [];
      byName.set(name, controls);
      groups.push({ controls, name });
    }
    byName.get(name)!.push(control);
  }

  return groups;
});

const openGroups = ref<Set<string>>(new Set());

// Reset the open set whenever the selection moves, so every element opens the same way.
watch(
  () => [props.node?.__builderId, groupedControls.value.map(g => g.name).join('|')].join('#'),
  () => {
    const names = groupedControls.value.map(g => g.name);
    const primary = names.filter(n => PRIMARY_GROUPS.includes(n));

    openGroups.value = new Set(primary.length ? primary : names.slice(0, OPEN_GROUP_COUNT));
  },
  { immediate: true },
);

function isOpen(name: string): boolean {
  // An unnamed group has no header to click, so it is always shown.
  return name === '' || openGroups.value.has(name);
}

function toggleGroup(name: string) {
  const next = new Set(openGroups.value);
  if (next.has(name)) next.delete(name);
  else next.add(name);
  openGroups.value = next;
}

const inertReason = computed(() => (props.node ? clickIsInert(props.node) : null));

const INERT_MESSAGE: Record<string, string> = {
  unwired: 'This does nothing when clicked — it is not wired to an action. Pick one under Behavior.',
};

const inertMessage = computed(() => (inertReason.value ? INERT_MESSAGE[inertReason.value] : ''));

/** The current type plus anything it can become, so the select reads as a type picker. */
const convertOptions = computed(() => {
  const targets = schema.value?.convertsTo ?? [];
  if (!targets.length || !props.node) return [];

  return [props.node.type, ...targets]
    .filter(type => props.catalog[type])
    .map(type => ({ label: props.catalog[type].displayName, value: type }));
});

const convertHint = computed(() => {
  if (!props.node) return '';

  const losses = (schema.value?.convertsTo ?? []).flatMap(target =>
    conversionLosses(props.node!, target, props.catalog),
  );

  if (!losses.length) return 'Wording, styling and behaviour carry over.';

  return `Wording, styling and behaviour carry over. Switching would drop: ${[...new Set(losses)].join(', ')}.`;
});

function convertTo(targetType: string) {
  if (props.node && targetType !== props.node.type) emit('convert', targetType);
}

function valueFor(control: ControlSchema): string | number | boolean | undefined {
  const source = control.target === 'parent' ? props.parent : props.node;

  return source?.attributes?.[control.attr];
}

function onChange(control: ControlSchema, value: string | number | boolean) {
  if (control.target === 'parent') return emit('change-parent', control.attr, value);

  emit('change', control.attr, value);
}
</script>
